import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid2,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import {
  MyLocation as MyLocationIcon,
  Search as SearchIcon,
  Place as PlaceIcon,
  Train as TrainIcon,
  LocalAirport as LocalAirportIcon,
  // FlightTakeoff as FlightIcon,
  DirectionsBus as DirectionsBusIcon,
  LocalTaxi as LocalTaxiIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Edit as EditIcon,
  AddLocationAlt as NewCityIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useGetStatesQuery } from "../../features/states/api/statesApiSlice";
import { useGetCitiesQuery } from "../../features/cities/api/citiesApiSlice";
import { useGetTransportTypesQuery } from "../../features/transportTypes/api/transportTypesApiSlice";

// --- Leaflet Icon Fix ---
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// --- Constants ---
const DISTANCE_RANGES = [
  { value: 200, label: "Walking distance (< 200m)" },
  { value: 500, label: "Short walk (200m - 500m)" },
  { value: 1000, label: "5-10 min walk (500m - 1km)" },
  { value: 2000, label: "Auto/Rickshaw needed (1km - 2km)" },
  { value: 5000, label: "Far (> 2km)" },
];

// 2. Icon Mapper Helper
const getTransportIcon = (iconName) => {
  switch (iconName) {
    case "SubwayIcon":
      return <TrainIcon fontSize="small" />;
    case "BusIcon":
      return <DirectionsBusIcon fontSize="small" />;
    case "TrainIcon":
      return <TrainIcon fontSize="small" />;
    case "FlightIcon":
      return <LocalAirportIcon fontSize="small" />;
    case "TaxiIcon":
      return <LocalTaxiIcon fontSize="small" />;
    default:
      return <PlaceIcon fontSize="small" />;
  }
};

// const TRANSPORT_TYPES = [
//   {
//     value: "metro",
//     label: "Metro Station",
//     icon: <TrainIcon fontSize="small" />,
//   },
//   {
//     value: "bus",
//     label: "Bus Stand",
//     icon: <DirectionsBusIcon fontSize="small" />,
//   },
//   {
//     value: "railway",
//     label: "Railway Station",
//     icon: <TrainIcon fontSize="small" />,
//   },
//   {
//     value: "auto",
//     label: "Shared Auto Stand",
//     icon: <LocalTaxiIcon fontSize="small" />,
//   },
// ];

const LocationForm = forwardRef(({ onDataChange, initialData }, ref) => {
  const PATNA_CENTER = [25.5941, 85.1376];
  const NOMINATIM_BASE_URL = "https://nominatim.openstreetmap.org";

  const {
    data: transportTypesData,
    isLoading: loadingTransport,
    isSuccess: isTransportSuccess,
  } = useGetTransportTypesQuery();

  // Process Transport Types for the dropdown
  const transportOptions = useMemo(() => {
    if (!isTransportSuccess || !transportTypesData) return [];
    return Array.isArray(transportTypesData)
      ? transportTypesData
      : Object.values(transportTypesData.entities || {});
  }, [transportTypesData, isTransportSuccess]);

  // --- Refs ---
  const fieldRefs = {
    map: useRef(null),
    buildingName: useRef(null),
    state: useRef(null),
    city: useRef(null),
    area: useRef(null),
    pincode: useRef(null),
    transportType: useRef(null),
    transportDistance: useRef(null),
  };

  // --- State ---
  const [mapCenter, setMapCenter] = useState(
    initialData?.lat ? [initialData.lat, initialData.lng] : PATNA_CENTER,
  );
  const [markerPosition, setMarkerPosition] = useState(
    initialData?.lat ? [initialData.lat, initialData.lng] : PATNA_CENTER,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isPinLoading, setIsPinLoading] = useState(false);

  // Soft Lock State: true = Editable (Unlocked), false = ReadOnly (Locked)
  const [isUnlocked, setIsUnlocked] = useState({
    area: false,
    pincode: false,
    state: false,
    city: false,
  });

  const [formData, setFormData] = useState({
    buildingName: initialData?.buildingName || "",
    streetAddress: initialData?.streetAddress || "",
    area: initialData?.area || "",
    countryId: "IN",
    stateId: initialData?.stateId || "",
    cityId: initialData?.cityId || null,
    cityName: initialData?.cityName || "",
    pincode: initialData?.pincode || "",
    lat: initialData?.lat || "",
    lng: initialData?.lng || "",
    nearestLandmark: initialData?.nearestLandmark || "",
    landmarkDistance: initialData?.landmarkDistance || "",
    transportTypeId: initialData?.transportTypeId || "", // Store the ObjectId
    transportName: initialData?.transportName || "",
    transportDistance: initialData?.transportDistance || "", // Stores Number
  });

  const [errors, setErrors] = useState({});

  // --- API Queries ---
  const {
    data: statesData,
    isSuccess: isStatesSuccess,
    // isLoading: isStatesLoading,
  } = useGetStatesQuery();

  // Helper to normalize States data (handles Array vs Entity Adapter)
  const stateOptions = useMemo(() => {
    if (!isStatesSuccess || !statesData) return [];
    return Array.isArray(statesData)
      ? statesData
      : Object.values(statesData.entities || {});
  }, [statesData, isStatesSuccess]);

  const {
    data: citiesData,
    isSuccess: isCitiesSuccess,
    isFetching: isCitiesFetching, // Useful for loading spinner
  } = useGetCitiesQuery(formData.stateId, {
    skip: !formData.stateId, // SKIP the query if no state is selected
    refetchOnMountOrArgChange: true, // Ensure it refetches when state changes
  });

  // --- Derived Data ---
  const availableCities = useMemo(() => {
    if (!isCitiesSuccess || !citiesData) return [];

    // The API already filtered by state, so just return the data
    return Array.isArray(citiesData)
      ? citiesData
      : citiesData.entities
        ? Object.values(citiesData.entities)
        : [];
  }, [citiesData, isCitiesSuccess]);

  //   // Memoize the cities list filtered by the selected state
  //   const availableCities = useMemo(() => {
  //     if (!isCitiesSuccess || !citiesData) return [];
  //     if (!formData.stateId) return []; // Only show cities if state is selected

  //     // Assuming citiesData is an array. If it's an entity object (like Redux Toolkit EntityAdapter),
  //     // you might need Object.values(citiesData.entities).
  //     const citiesArray = Array.isArray(citiesData)
  //       ? citiesData
  //       : citiesData.entities
  //       ? Object.values(citiesData.entities)
  //       : [];

  //     return citiesArray.filter((city) => city.stateId === formData.stateId);
  //   }, [citiesData, isCitiesSuccess, formData.stateId]);

  const handleDataChange = (newData) => {
    const updatedData = { ...formData, ...newData };
    setFormData(updatedData);
    onDataChange(updatedData);

    Object.keys(newData).forEach((key) => {
      if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
    });
  };

  // --- Helpers to Match Map Data to DB ---
  //   const findStateInDb = (detectedStateName) => {
  //     if (!isStatesSuccess || !statesData || !detectedStateName) return null;

  //     // Handle Redux EntityAdapter structure or standard array
  //     const statesArray = Array.isArray(statesData)
  //       ? statesData
  //       : Object.values(statesData.entities);

  //     const search = detectedStateName.toLowerCase();
  //     return statesArray.find((s) => s.name.toLowerCase() === search);
  //   };

  const findStateInDb = (detectedStateName, isoCode) => {
    if (!isStatesSuccess || !statesData) return null;

    const statesArray = Array.isArray(statesData)
      ? statesData
      : Object.values(statesData.entities);

    const searchName = detectedStateName?.toLowerCase();

    // 1. Try ISO Mapping first (most reliable)
    // if (isoCode && isoCode.startsWith("IN-")) {
    //   const code = isoCode.split("-")[1];
    //   const mappedName = STATE_ISO_MAP[code]?.toLowerCase();
    //   const match = statesArray.find(
    //     (s) => s.name.toLowerCase() === mappedName
    //   );
    //   if (match) return match;
    // }

    if (isoCode) {
      const match = statesArray.find((s) => s.id === isoCode);
      if (match) return match;
    }

    // 2. Try direct match
    if (searchName) {
      const directMatch = statesArray.find(
        (s) => s.name.toLowerCase() === searchName,
      );
      if (directMatch) return directMatch;

      // 3. Try fuzzy match (e.g. "NCT of Delhi" matches "Delhi")
      const fuzzyMatch = statesArray.find(
        (s) =>
          searchName.includes(s.name.toLowerCase()) ||
          s.name.toLowerCase().includes(searchName),
      );
      if (fuzzyMatch) return fuzzyMatch;
    }

    return null;
  };

  const findCityInDb = (detectedCityName, stateId) => {
    if (!isCitiesSuccess || !citiesData || !detectedCityName) return null;

    const citiesArray = Array.isArray(citiesData)
      ? citiesData
      : citiesData.entities
        ? Object.values(citiesData.entities)
        : [];

    const search = detectedCityName.toLowerCase();

    // Strict Filter: Must match name AND the detected State
    return citiesArray.find(
      (c) => c.stateId === stateId && c.name.toLowerCase() === search,
    );
  };

  // --- Geocoding Logic ---
  const handleSearchChange = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      setIsSearching(true);
      const res = await fetch(
        `${NOMINATIM_BASE_URL}/search?format=json&q=${query}&countrycodes=in&addressdetails=1&limit=5`,
      );
      const data = await res.json();
      setSuggestions(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSearching(false);
    }
  };

  const fetchAddressFromCoords = useCallback(
    async (lat, lng) => {
      try {
        setIsPinLoading(true);
        const res = await fetch(
          `${NOMINATIM_BASE_URL}/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        );
        const data = await res.json();
        const addr = data.address;
        // console.log(111, addr);

        // Extract Data
        const detectedCity =
          addr.city ||
          addr.town ||
          addr.village ||
          //   addr.state_district ||
          addr.county ||
          "";
        const detectedState = addr.state || addr.province || "";
        const detectedArea =
          addr.suburb || addr.neighbourhood || addr.residential || "";
        const detectedPincode = addr.postcode || "";
        const isoCode = addr["ISO3166-2-lvl4"]; // Extract the ISO code

        // 1. Try to Match State
        const matchedState = findStateInDb(detectedState, isoCode);
        const newStateId = matchedState ? matchedState.id : "";

        // 2. Try to Match City (Only if we found a state)
        let matchedCity = null;
        if (newStateId) {
          matchedCity = findCityInDb(detectedCity, newStateId);
        }

        // 3. Prepare Update
        const updateData = {
          lat,
          lng,
          area: detectedArea,
          pincode: detectedPincode,
          stateId: newStateId,
          // If matched, use ID. If not, use text (New City Logic)
          cityId: matchedCity ? matchedCity.id : null,
          cityName: matchedCity ? matchedCity.name : detectedCity,
        };

        handleDataChange(updateData);
        setSearchQuery(data.display_name || "Pinned Location");

        // 4. "Soft Lock" the fields so user knows they came from the map
        setIsUnlocked({
          //   area: false, // Locked
          //   pincode: false, // Locked
          //   state: false, // Locked
          //   city: false, // Locked
          //   state: !!matchedState,
          //   city: !!detectedCity,
          //   area: !detectedArea, // If no area found, leave it unlocked for user
          //   pincode: !detectedPincode,
          // If matchedState exists, set to false (Locked). If NOT found, set to true (Unlocked).
          state: !matchedState,
          // If matchedCity exists, set to false (Locked).
          city: !matchedCity,
          // If area text was found, set to false (Locked). If empty, true (Unlocked).
          area: !detectedArea,
          // If pincode was found, set to false (Locked).
          pincode: !detectedPincode,
        });
      } catch (err) {
        console.error("Reverse geocode failed", err);
        // On error, unlock everything so user isn't stuck
        setIsUnlocked({ area: true, pincode: true, state: true, city: true });
      } finally {
        setIsPinLoading(false);
      }
    },
    [statesData, citiesData, isStatesSuccess, isCitiesSuccess],
  );

  // --- Handlers ---
  const handleStateChange = (e) => {
    handleDataChange({
      stateId: e.target.value,
      cityId: null, // Reset city on state change
      cityName: "",
    });
  };

  const handleCityChange = (event, newValue) => {
    if (typeof newValue === "string") {
      // User typed a new city name
      handleDataChange({ cityName: newValue, cityId: null });
    } else if (newValue && newValue.id) {
      // User selected from list
      handleDataChange({ cityName: newValue.name, cityId: newValue.id });
    } else {
      // Cleared
      handleDataChange({ cityName: "", cityId: null });
    }
  };

  const toggleLock = (field) => {
    setIsUnlocked((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // --- Map Components ---
  const MapEvents = () => {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        setMarkerPosition([lat, lng]);
        setMapCenter([lat, lng]);
        fetchAddressFromCoords(lat, lng);
      },
    });
    return null;
  };

  const FlyToLocation = ({ center }) => {
    const map = useMap();
    useEffect(() => {
      if (center) map.flyTo(center, 15);
    }, [center, map]);
    return null;
  };

  const selectSuggestion = (item) => {
    const lat = parseFloat(item.lat);
    const lon = parseFloat(item.lon);
    setMarkerPosition([lat, lon]);
    setMapCenter([lat, lon]);
    setSuggestions([]);
    fetchAddressFromCoords(lat, lon);
  };

  const handleCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        const { latitude, longitude } = pos.coords;
        setMarkerPosition([latitude, longitude]);
        setMapCenter([latitude, longitude]);
        fetchAddressFromCoords(latitude, longitude);
      });
    }
  };

  // NEW: Effect to match City ID *after* cities are fetched
  useEffect(() => {
    // Only run if we have city text, but no ID, and data just loaded
    // 1. Guard: Only run if we have a name but no ID, and cities are loaded
    if (
      formData.cityName &&
      !formData.cityId &&
      isCitiesSuccess &&
      availableCities.length > 0
    ) {
      const normalizedSearch = formData.cityName.toLowerCase();

      //   const matchedCity = availableCities.find(
      //     (c) => c.name.toLowerCase() === normalizedSearch
      //   );
      // 2. The Logic Switch
      //   let matchedCity = null;

      //   if (!isUnlocked.city) {
      //     // CASE A: FIELD IS LOCKED (Map Pin logic)
      //     // Use aggressive substring matching to bridge "New Delhi" vs "Delhi"
      //     matchedCity = availableCities.find((c) => {
      //       const dbName = c.name.toLowerCase();
      //       return (
      //         dbName === normalizedSearch ||
      //         normalizedSearch.includes(dbName) ||
      //         dbName.includes(normalizedSearch)
      //       );
      //     });
      //   } else {
      //     // CASE B: FIELD IS UNLOCKED (User is typing)
      //     // ONLY match if the name is exactly what is in the list.
      //     // This prevents "Ga" from locking onto "Gaya" while typing.
      //     matchedCity = availableCities.find(
      //       (c) => c.name.toLowerCase() === normalizedSearch
      //     );
      //   }

      const matchedCity = availableCities.find((c) => {
        const dbName = c.name.toLowerCase();
        const synonyms = (c.synonyms || []).map((s) =>
          String(s || "")
            .toLowerCase()
            .trim(),
        );
        // Match if identical OR if one is a sub-string of the other (Synonym logic)
        // e.g., "New Delhi" contains "Delhi"
        return (
          dbName === normalizedSearch ||
          //   normalizedSearch.includes(dbName) ||
          //   dbName.includes(normalizedSearch)
          synonyms.includes(normalizedSearch)
        );
      });

      // 3. Apply the match
      if (matchedCity) {
        // We found a match in the newly loaded list! Update the ID.
        handleDataChange({
          cityId: matchedCity.id,
          cityName: matchedCity.name, // correct case (e.g. "gaya" -> "Gaya")
        });

        // Lock the field since we verified it
        setIsUnlocked((prev) => ({ ...prev, city: false }));
      }
    }
  }, [
    // citiesData,
    availableCities,
    isCitiesSuccess,
    formData.cityName,
    formData.cityId,
    handleDataChange,
  ]);

  //   useEffect(() => {
  //     if (
  //       formData.cityName &&
  //       !formData.cityId &&
  //       isCitiesSuccess &&
  //       availableCities.length > 0
  //     ) {
  //       const search = formData.cityName.toLowerCase();

  //       const matchedCity = availableCities.find((c) => {
  //         const dbName = c.name.toLowerCase();
  //         // Match if identical OR if one is a sub-string of the other (Synonym logic)
  //         // e.g., "New Delhi" contains "Delhi"
  //         return (
  //           dbName === search ||
  //           search.includes(dbName) ||
  //           dbName.includes(search)
  //         );
  //       });

  //       if (matchedCity) {
  //         handleDataChange({
  //           cityId: matchedCity.id,
  //           cityName: matchedCity.name, // Use the DB's official name
  //         });
  //         setIsUnlocked((prev) => ({ ...prev, city: false })); // Lock it
  //       }
  //     }
  //   }, [
  //     availableCities,
  //     isCitiesSuccess,
  //     formData.cityName,
  //     formData.cityId,
  //     handleDataChange,
  //   ]);

  // --- Validation ---
  useImperativeHandle(ref, () => ({
    validateUserInput: () => {
      const newErrors = {};
      let isValid = true;
      let firstErrorKey = null;

      if (!formData.lat || !formData.lng) {
        // newErrors.map = "Please drop a pin on the map to confirm location.";
        newErrors.map =
          "Search or drop a pin to auto-fill address details. If exact house isn't visible, pin the nearest street";
        isValid = false;
        firstErrorKey = "map";
      }
      if (!formData.buildingName?.trim()) {
        newErrors.buildingName = "Building Name is required";
        isValid = false;
        if (!firstErrorKey) firstErrorKey = "buildingName";
      }
      if (!formData.stateId) {
        newErrors.state = "State / UT is required";
        isValid = false;
        if (!firstErrorKey) firstErrorKey = "state";
      }
      if (!formData.cityName?.trim()) {
        newErrors.city = "City is required";
        isValid = false;
        if (!firstErrorKey) firstErrorKey = "city";
      }
      if (!formData.area?.trim()) {
        newErrors.area = "Area is required";
        isValid = false;
        if (!firstErrorKey) firstErrorKey = "area";
      }
      if (
        !formData.pincode?.trim() ||
        !/^\d{6}$/.test(formData.pincode.trim())
      ) {
        newErrors.pincode = "Valid 6-digit Pincode required";
        isValid = false;
        if (!firstErrorKey) firstErrorKey = "pincode";
      }
      if (!formData.transportTypeId) {
        newErrors.transportTypeId = "Transport Type is required";
        isValid = false;
        if (!firstErrorKey) firstErrorKey = "transportType";
      }
      if (!formData.transportDistance) {
        newErrors.transportDistance = "Distance is required";
        isValid = false;
        if (!firstErrorKey) firstErrorKey = "transportDistance";
      }

      setErrors(newErrors);

      if (!isValid && firstErrorKey) {
        const element = fieldRefs[firstErrorKey]?.current;
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          if (element.focus) element.focus();
        }
      }
      return isValid;
    },
    getFormData: () => formData,
  }));

  // --- Render Helpers for Locked/Unlocked Fields ---
  //   const renderLockIcon = ({ field, hasValue }) => {
  //     if (!hasValue) return null; // Don't show lock if field is empty
  //     const isFieldUnlocked = isUnlocked[field];
  //     return (
  //       <Tooltip
  //         title={
  //           isFieldUnlocked
  //             ? "Unlocked for editing"
  //             : "Auto-filled from Map. Click to edit."
  //         }
  //       >
  //         <IconButton
  //           onClick={() => toggleLock(field)}
  //           size="small"
  //           edge="end"
  //           //   sx={{
  //           //     p: "4px", // tighter padding
  //           //     // force identical SVG size across icons
  //           //     "& svg": { fontSize: 18, lineHeight: 0 },
  //           //   }}
  //         >
  //           {isFieldUnlocked ? (
  //             <LockOpenIcon color="primary" fontSize="small" />
  //           ) : (
  //             <LockIcon color="success" fontSize="small" />
  //           )}
  //         </IconButton>
  //       </Tooltip>
  //     );
  //   };

  // REUSABLE LOCK TOGGLE COMPONENT
  const LockToggle = ({ field, hasValue }) => {
    if (!hasValue) return null; // Don't show lock if field is empty
    const unlocked = isUnlocked[field];
    return (
      <Tooltip
        title={
          unlocked
            ? "Unlocked: Manually editing"
            : "Locked: Matches Map location"
        }
      >
        <IconButton
          size="small"
          //   onClick={() => setIsUnlocked((p) => ({ ...p, [field]: !p[field] }))}
          onClick={() => toggleLock(field)}
          sx={{ p: "4px" }}
        >
          {unlocked ? (
            <LockOpenIcon fontSize="small" color="primary" />
          ) : (
            <LockIcon fontSize="small" color="success" />
          )}
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 800,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* MAP SECTION */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }} ref={fieldRefs.map}>
        <Typography
          variant="h6"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
            mb: 2,
          }}
        >
          <PlaceIcon color="primary" /> Pin Your Location
        </Typography>

        <Box sx={{ position: "relative", mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search Area (e.g. Connaught Place, New Delhi)"
            value={searchQuery}
            onChange={handleSearchChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <IconButton onClick={handleCurrentLocation} color="primary">
                    <MyLocationIcon />
                  </IconButton>
                ),
              },
            }}
          />
          {suggestions.length > 0 && (
            <Paper
              sx={{
                position: "absolute",
                zIndex: 1000,
                width: "100%",
                maxHeight: 200,
                overflow: "auto",
                mt: 0.5,
              }}
            >
              <List>
                {suggestions.map((item) => (
                  <ListItem
                    button
                    key={item.place_id}
                    onClick={() => selectSuggestion(item)}
                  >
                    <ListItemText primary={item.display_name} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>

        <Box
          sx={{
            height: 400,
            borderRadius: 2,
            overflow: "hidden",
            border: errors.map ? "2px solid red" : "1px solid #ddd",
            position: "relative",
          }}
        >
          {isPinLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(255,255,255,0.7)",
                zIndex: 999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <MapContainer
            center={mapCenter}
            zoom={13}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap"
            />
            <MapEvents />
            <FlyToLocation center={mapCenter} />
            <Marker
              position={markerPosition}
              draggable={true}
              eventHandlers={{
                dragend: (e) => {
                  const { lat, lng } = e.target.getLatLng();
                  setMarkerPosition([lat, lng]);
                  setMapCenter([lat, lng]);
                  fetchAddressFromCoords(lat, lng);
                },
              }}
            >
              <Popup>PG Location</Popup>
            </Marker>
          </MapContainer>
        </Box>
        {errors.map ? (
          <Typography
            color="error"
            variant="caption"
            sx={{ mt: 1, display: "block" }}
          >
            {errors.map}
          </Typography>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            * Search or drop a pin to auto-fill address details. If exact house
            isn't visible, pin the nearest street.
          </Typography>
        )}
      </Paper>

      {/* ADDRESS SECTION */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Address Details
        </Typography>

        {/* User Instruction Alert */}
        <Alert
          severity="info"
          icon={<InfoIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
        >
          Fields with{" "}
          <LockIcon
            fontSize="small"
            color="success"
            sx={{ verticalAlign: "text-bottom" }}
          />{" "}
          are auto-filled from Map. Click to unlock{" "}
          <LockOpenIcon
            fontSize="small"
            color="primary"
            sx={{ verticalAlign: "text-bottom" }}
          />{" "}
          and edit.
        </Alert>

        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              inputRef={fieldRefs.buildingName}
              fullWidth
              label="Building / House Name"
              value={formData.buildingName}
              required
              onChange={(e) =>
                handleDataChange({ buildingName: e.target.value })
              }
              error={!!errors.buildingName}
              helperText={errors.buildingName}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Street / Main Road"
              value={formData.streetAddress}
              onChange={(e) =>
                handleDataChange({ streetAddress: e.target.value })
              }
            />
          </Grid2>

          {/* STATE */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl
              fullWidth
              error={!!errors.state}
              required
              ref={fieldRefs.state}
            >
              <InputLabel>State / Union Territory</InputLabel>
              <Select
                // value={formData.stateId || ""}
                value={stateOptions.length > 0 ? formData.stateId : ""}
                label="State / Union Territory"
                onChange={handleStateChange}
                readOnly={!isUnlocked.state && !!formData.stateId} // ReadOnly if locked AND has value
                sx={{
                  bgcolor:
                    !isUnlocked.state && formData.stateId
                      ? "#f0f7ff"
                      : "inherit",
                }}
                endAdornment={
                  <InputAdornment position="end" sx={{ mr: 2 }}>
                    {/* {renderLockIcon("state", !!formData.stateId)} */}
                    <LockToggle field="state" hasValue={!!formData.stateId} />
                  </InputAdornment>
                }
              >
                <MenuItem value="">-- Select State / UT --</MenuItem>
                {stateOptions.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
                {/* {isStatesSuccess &&
                  (Array.isArray(statesData)
                    ? statesData
                    : Object.values(statesData.entities)
                  ).map((state) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.name}
                    </MenuItem>
                  ))} */}
              </Select>
              {errors.state && <FormHelperText>{errors.state}</FormHelperText>}
            </FormControl>
          </Grid2>

          {/* CITY */}
          <Grid2 size={{ xs: 12, md: 6 }} ref={fieldRefs.city}>
            <Autocomplete
              freeSolo
              disableClearable
              disabled={!formData.stateId}
              options={availableCities}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.name
              }
              value={formData.cityName}
              loading={isCitiesFetching}
              onChange={handleCityChange}
              onInputChange={(event, newInputValue) => {
                if (event)
                  handleDataChange({ cityName: newInputValue, cityId: null });
              }}
              readOnly={!isUnlocked.city && !!formData.cityId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  required
                  error={!!errors.city}
                  //   helperText={errors.city}
                  helperText={
                    !formData.stateId ? "Select State first" : errors.city
                  }
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      readOnly: !isUnlocked.city && !!formData.cityId,
                      endAdornment: (
                        <>
                          {isCitiesFetching ? (
                            <CircularProgress size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                          {/* Custom Status Icons */}
                          <InputAdornment position="end" sx={{ mr: 0.6 }}>
                            {/* {isUnlocked.city ? (
                              //   <Tooltip title="Unlocked">
                              //     <EditIcon
                              //       color="primary"
                              //       onClick={() => toggleLock("city")}
                              //       sx={{ cursor: "pointer" }}
                              //     />
                              //   </Tooltip>
                              renderLockIcon("city")
                            ) :  */}
                            {formData.cityId ? (
                              //   <Tooltip title="Verified from Database">
                              //     <LockIcon
                              //       color="success"
                              //       onClick={() => toggleLock("city")}
                              //       sx={{ cursor: "pointer" }}
                              //     />
                              //   </Tooltip>
                              //   renderLockIcon("city")
                              <LockToggle field="city" hasValue={true} />
                            ) : formData.cityName ? (
                              <Tooltip title="New City Detected">
                                <NewCityIcon color="warning" fontSize="small" />
                              </Tooltip>
                            ) : null}
                          </InputAdornment>
                        </>
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      bgcolor:
                        !isUnlocked.city && formData.cityId
                          ? "#f0f7ff"
                          : "inherit",
                    },
                  }}
                />
              )}
            />
            {/* Warning for new city */}
            {!formData.cityId &&
              formData.cityName &&
              formData.cityName.length > 2 && (
                <Alert
                  severity="warning"
                  icon={<WarningIcon fontSize="inherit" />}
                  sx={{ mt: 1, py: 0, fontSize: "0.75rem" }}
                >
                  New City detected. We will verify "{formData.cityName}"
                  shortly after your submit.
                </Alert>
              )}
          </Grid2>

          {/* AREA */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              inputRef={fieldRefs.area}
              fullWidth
              label="Area / Locality"
              value={formData.area}
              onChange={(e) => handleDataChange({ area: e.target.value })}
              required
              error={!!errors.area}
              helperText={errors.area}
              slotProps={{
                input: {
                  readOnly: !isUnlocked.area && !!formData.area,
                  endAdornment: (
                    <InputAdornment position="end">
                      {/* {renderLockIcon("area")} */}
                      <LockToggle field="area" hasValue={!!formData.area} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiInputBase-root": {
                  bgcolor:
                    !isUnlocked.area && formData.area ? "#f0f7ff" : "inherit",
                },
              }}
            />
          </Grid2>

          {/* PINCODE */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              inputRef={fieldRefs.pincode}
              fullWidth
              label="Pincode"
              value={formData.pincode}
              onChange={(e) => handleDataChange({ pincode: e.target.value })}
              required
              error={!!errors.pincode}
              helperText={errors.pincode}
              slotProps={{
                htmlInput: { maxLength: 6 },
                input: {
                  readOnly: !isUnlocked.pincode && !!formData.pincode,
                  endAdornment: (
                    <InputAdornment position="end">
                      {/* {renderLockIcon("pincode")} */}
                      <LockToggle
                        field="pincode"
                        hasValue={!!formData.pincode}
                      />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiInputBase-root": {
                  bgcolor:
                    !isUnlocked.pincode && formData.pincode
                      ? "#f0f7ff"
                      : "inherit",
                },
              }}
            />
          </Grid2>
        </Grid2>
      </Paper>

      {/* CONNECTIVITY SECTION */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Connectivity
        </Typography>

        <Grid2 container spacing={2}>
          {/* Nearest Transport Type (From DB) */}
          <Grid2 size={{ xs: 12, md: 4 }}>
            <FormControl
              fullWidth
              error={!!errors.transportTypeId}
              // disabled={loadingTransport}
              required
            >
              <InputLabel>Nearest Transport</InputLabel>
              <Select
                name="transportType"
                // value={formData.transportTypeId || ""}
                value={transportOptions?.length ? formData.transportTypeId : ""}
                label="Nearest Transport"
                onChange={(e) =>
                  handleDataChange({ transportTypeId: e.target.value })
                }
                endAdornment={
                  loadingTransport && (
                    <InputAdornment position="end" sx={{ mr: 2 }}>
                      <CircularProgress size={20} />
                    </InputAdornment>
                  )
                }
              >
                <MenuItem value="">-- Select Transport Type --</MenuItem>
                {isTransportSuccess &&
                  //   <MenuItem disabled>
                  //     <CircularProgress size={20} />
                  //   </MenuItem>
                  // ) : (
                  transportOptions.map((t) => (
                    <MenuItem key={t._id || t.id} value={t._id || t.id}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        {getTransportIcon(t.icon)}
                        {t.name}
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
              {errors.transportTypeId && (
                <FormHelperText>{errors.transportTypeId}</FormHelperText>
              )}
            </FormControl>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Station Name"
              value={formData.transportName}
              onChange={(e) =>
                handleDataChange({ transportName: e.target.value })
              }
              placeholder="e.g. Rajiv Chowk"
            />
          </Grid2>

          {/* <Grid2 size={{ xs: 12, md: 4 }}>
            <FormControl
              fullWidth
              error={!!errors.transportType}
              ref={fieldRefs.transportType}
            >
              <InputLabel>Nearest Transport</InputLabel>
              <Select
                value={formData.transportType || ""}
                label="Nearest Transport"
                onChange={(e) =>
                  handleDataChange({ transportType: e.target.value })
                }
              >
                {TRANSPORT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      {t.icon} {t.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.transportType && (
                <FormHelperText>{errors.transportType}</FormHelperText>
              )}
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Station Name"
              value={formData.transportName}
              onChange={(e) =>
                handleDataChange({ transportName: e.target.value })
              }
              placeholder="e.g. Rajiv Chowk"
            />
          </Grid2> */}

          {/* Numeric Distance Select */}
          <Grid2 size={{ xs: 12, md: 4 }}>
            <FormControl
              fullWidth
              error={!!errors.transportDistance}
              ref={fieldRefs.transportDistance}
              required
            >
              <InputLabel>Distance</InputLabel>
              <Select
                value={formData.transportDistance || ""}
                label="Distance"
                onChange={(e) =>
                  handleDataChange({ transportDistance: e.target.value })
                }
              >
                <MenuItem value="">-- Select Distance --</MenuItem>
                {DISTANCE_RANGES.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.transportDistance && (
                <FormHelperText>{errors.transportDistance}</FormHelperText>
              )}
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 8 }}>
            <TextField
              fullWidth
              label="Nearby Landmark"
              placeholder="e.g. Behind Apollo Hospital"
              value={formData.nearestLandmark}
              onChange={(e) =>
                handleDataChange({ nearestLandmark: e.target.value })
              }
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Landmark Distance</InputLabel>
              <Select
                value={formData.landmarkDistance || ""}
                onChange={(e) =>
                  handleDataChange({ landmarkDistance: e.target.value })
                }
                label="Landmark Distance"
              >
                <MenuItem value="">-- Select Distance --</MenuItem>
                {DISTANCE_RANGES.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
        </Grid2>
      </Paper>
    </Box>
  );
});

export default LocationForm;
